import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Pagination,
  useMediaQuery,
  useTheme,
  Table as MUITable,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  CalendarToday as CalendarTodayIcon,
  Group as GroupIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const recordsPerPage = 10;

const AttendancePage = () => {
  // ─── Data states ────────────────────────────────────────────────────────
  const [fullRecords, setFullRecords] = useState([]); // Full punch details
  const [minimalRecords, setMinimalRecords] = useState([]); // [{ username, date }]
  const [employeeList, setEmployeeList] = useState([]);

  // ─── Filter + Summary states ────────────────────────────────────────────
  const [filters, setFilters] = useState({
    employee: "all",                      // “all” or specific employee name
    monthYear: moment().format("YYYY-MM"), // Default: current month/year
  });
  const [summary, setSummary] = useState([]);

  // ─── Tabs ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ─── Pagination for daily view ─────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ─── Snackbar for export feedback ──────────────────────────────────────
  const [snackOpen, setSnackOpen] = useState(false);

  // ─── Fetch employees and punches on mount ──────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const cfg = { headers: { Authorization: `Bearer ${token}` } };

        // 1) Fetch employees
        const empRes = await axios.get(
          "https://attendanceapi.gensparc.com/employee/list",
          cfg
        );
        setEmployeeList(empRes.data);

        // 2) Fetch punch records
        const punchRes = await axios.get(
          "https://attendanceapi.gensparc.com/punch/list",
          cfg
        );
        let punches = punchRes.data;
        if (!Array.isArray(punches) && punches.data) punches = punches.data;

        // 3) (Optional) Fetch shift
        let shiftName = "Default Shift";
        try {
          const shiftRes = await axios.get(
            "https://attendanceapi.gensparc.com/shift/shiftlist",
            cfg
          );
          shiftName = shiftRes.data[0]?.shift_name || shiftName;
        } catch {
          // Keep “Default Shift” if error
        }

        // 4) Build fullRecords
        const full = punches.map((p) => {
          const inM = moment(p.checkIn);
          const outM = p.checkOut ? moment(p.checkOut) : null;
          const emp = empRes.data.find(
            (e) =>
              e.employee_name.trim().toLowerCase() ===
              p.username.trim().toLowerCase()
          );
          return {
            id: p.id,
            date: inM.format("YYYY-MM-DD"),
            employee: p.username.trim(),
            currentStatus: outM ? "Checked Out" : "Checked In",
            checkInTime: inM.format("HH:mm"),
            checkOutTime: outM ? outM.format("HH:mm") : "",
            address: p.checkInLocation,
            shift: shiftName,
            department: emp?.department || "",
            role: emp?.role || "",
            totalTime: p.Hours || "",
          };
        });
        setFullRecords(full);

        // 5) Build minimalRecords = [{ username, date }]
        const minimal = full.map((r) => ({
          username: r.employee.trim().toLowerCase(),
          date: r.date,
        }));
        setMinimalRecords(minimal);
      } catch (err) {
        console.error("Data fetch error:", err);
      }
    };

    fetchData();
  }, []);

  // ─── Determine whether a given date is a working day ────────────────────
  // const isWorkingDay = (mDate) => {
  //   const day = mDate.day(); // Sunday=0, Saturday=6
  //   if (day === 0) return false; // Sunday off
  //   if (day !== 6) return true; // Mon–Fri
  //   const weekOfMonth = Math.ceil(mDate.date() / 7);
  //   return weekOfMonth === 2 || weekOfMonth === 4; // 2nd & 4th Saturdays
  // };


  const isWorkingDay = (mDate) => {
  const day = mDate.day(); // Sunday=0, Monday=1, ... Saturday=6
  // If Saturday (6) or Sunday (0) => off
  if (day === 0 || day === 6) return false;

  // All other days (Mon–Fri) => working
  return true;
};


  // ─── Apply Filters → Build summary array for the selected month/year ───
  const applyFilters = () => {
    const { employee, monthYear } = filters;
    if (!monthYear) {
      alert("Please select a month and year.");
      return;
    }

    // Parse “YYYY-MM” → first of that month
    const start = moment(`${monthYear}-01`, "YYYY-MM-DD");
    const monthLength = start.daysInMonth(); // 28, 29, 30, or 31

    // Determine “today” to clamp current month
    const today = moment();
    const isCurrentMonth = start.isSame(today, "month");

    // Build all working dates in that month up to “today” if current
    const allDates = [];
    for (let dayNum = 1; dayNum <= monthLength; dayNum++) {
      const d = start.clone().date(dayNum);
      if (isWorkingDay(d)) {
        if (isCurrentMonth) {
          if (d.isSameOrBefore(today, "day")) {
            allDates.push(d.format("YYYY-MM-DD"));
          }
        } else {
          allDates.push(d.format("YYYY-MM-DD"));
        }
      }
    }

    // For each employee, compute presentDates & absentDates within that month
    const rawSummary = employeeList.map((emp) => {
      const nameKey = emp.employee_name.trim().toLowerCase();
      const userDates = minimalRecords
        .filter((r) => r.username === nameKey)
        .map((r) => r.date)
        .filter((dt) => {
          // Only keep dates that lie within this month’s [1..monthLength]
          const m = moment(dt, "YYYY-MM-DD");
          return (
            m.isSameOrAfter(start, "day") &&
            m.isSameOrBefore(start.clone().date(monthLength), "day")
          );
        });
      const uniqueDates = Array.from(new Set(userDates));

      const presentDates = allDates.filter((d) => uniqueDates.includes(d));
      const absentDates = allDates.filter((d) => !uniqueDates.includes(d));

      return {
        employee: emp.employee_name.trim(),
        presentDates,
        absentDates,
      };
    });

    // If a single employee is selected, filter to just that one
    let filteredSummary = rawSummary;
    if (employee !== "all") {
      filteredSummary = rawSummary.filter(
        (r) =>
          r.employee.trim().toLowerCase() === employee.trim().toLowerCase()
      );
    }

    // Sort alphabetically (if multiple employees)
    filteredSummary.sort((a, b) =>
      a.employee.localeCompare(b.employee, undefined, { sensitivity: "base" })
    );

    setSummary(filteredSummary);
  };

  // ─── When filters.employee changes (and summary exists), re‐apply ─────────
  useEffect(() => {
    if (summary.length > 0) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.employee]);

  const resetFilters = () => {
    setFilters({
      employee: "all",
      monthYear: moment().format("YYYY-MM"),
    });
    setSummary([]);
  };

  // ─── Export summary to Excel ───────────────────────────────────────────
  const exportToExcel = () => {
    if (!Array.isArray(summary) || summary.length === 0) {
      alert("No data to export.");
      return;
    }
    const exportRows = summary.map((r) => ({
      Employee: r.employee,
      "Present Days (Count)": (r.presentDates || []).length,
      "Present Dates": (r.presentDates || []).join(", "),
      "Absent Days (Count)": (r.absentDates || []).length,
      "Absent Dates": (r.absentDates || []).join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "attendance_summary.xlsx"
    );
    setSnackOpen(true);
  };

  // ─── Build a date→records map once ─────────────────────────────────────
  const recordsByDate = useMemo(() => {
    return fullRecords.reduce((acc, rec) => {
      if (!acc[rec.date]) acc[rec.date] = [];
      acc[rec.date].push(rec);
      return acc;
    }, {});
  }, [fullRecords]);

  // ─── Compute unique dates sorted descending ─────────────────────────────
  const uniqueDates = useMemo(() => {
    return Object.keys(recordsByDate).sort(
      (a, b) => new Date(b) - new Date(a)
    );
  }, [recordsByDate]);

  // ─── Pagination based on dates (10 dates per page) ────────────────────
  const totalPages = Math.ceil(uniqueDates.length / recordsPerPage);
  const datesForPage = useMemo(() => {
    const startIdx = (currentPage - 1) * recordsPerPage;
    const endIdx = currentPage * recordsPerPage;
    return uniqueDates.slice(startIdx, endIdx);
  }, [uniqueDates, currentPage]);

  // ─── Responsive column hiding for daily table ──────────────────────────
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <div className="container-fluid px-0 py-4" style={{ maxWidth: "1290px" }}>
      {/* ───── Tabs ─────────────────────────────────────────────────── */}
      <div className="card shadow-sm mb-2">
        <div className="card-body">
          <Tabs
            sx={{ marginTop: "25px" }}
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab
              label={
                <span>
                  <CalendarTodayIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Attendance
                </span>
              }
            />
            <Tab
              label={
                <span>
                  <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Summary View
                </span>
              }
            />
          </Tabs>
        </div>
      </div>

      {/* ───── Summary Tab ───────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div className="card shadow mb-4">
          <div className="card-body">
            <Typography variant="h5" gutterBottom className="mb-3">
              <GroupIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Team Attendance Summary
            </Typography>

            {/* ─ Filters ─ */}
            <div className="row g-3 mb-3 align-items-end">
              {/* Employee Selector */}
              <div className="col-md-2">
                <label className="form-label">Employee</label>
                <select
                  className="form-select"
                  value={filters.employee}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      employee: e.target.value,
                    }))
                  }
                >
                  <option value="all">All</option>
                  {employeeList.map((emp) => (
                    <option
                      key={emp.employee_name}
                      value={emp.employee_name.trim()}
                    >
                      {emp.employee_name.trim()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month-Year Picker */}
              <div className="col-md-2">
                <label className="form-label">Month &amp; Year</label>
                <input
                  type="month"
                  className="form-control"
                  value={filters.monthYear}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      monthYear: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Buttons */}
              <div className="col-md-4 d-flex justify-content-start">
                <button
                  className="btn btn-primary me-2"
                  onClick={applyFilters}
                >
                  Apply
                </button>
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={resetFilters}
                >
                  Reset
                </button>
                <button className="btn btn-success" onClick={exportToExcel}>
                  <FileDownloadIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                  export
                </button>
              </div>
            </div>

            {/* ─ Summary Display ─ */}
            {Array.isArray(summary) && summary.length > 0 ? (
              <>
                {filters.employee === "all" ? (
                  // When “All” is selected → one table for all employees
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <MUITable size="medium" padding="normal">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Employee
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Present/Absent (in days)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.map((row) => {
                          const presentCount = (row.presentDates || []).length;
                          const absentCount = (row.absentDates || []).length;

                          // Merge present + absent into one array of { date, present }
                          const combined = [
                            ...(row.presentDates || []).map((d) => ({
                              date: d,
                              present: true,
                            })),
                            ...(row.absentDates || []).map((d) => ({
                              date: d,
                              present: false,
                            })),
                          ];
                          combined.sort((a, b) => {
                            return (
                              moment(a.date, "YYYY-MM-DD").date() -
                              moment(b.date, "YYYY-MM-DD").date()
                            );
                          });

                          return (
                            <TableRow key={row.employee}>
                              <TableCell sx={{ py: 1.5 }}>
                                {row.employee}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {presentCount}/{absentCount}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {combined.map((obj) => {
                                  const dayNumber = moment(
                                    obj.date,
                                    "YYYY-MM-DD"
                                  ).date();
                                  return (
                                    <Chip
                                      key={obj.date}
                                      label={dayNumber}
                                      size="small"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                      color={obj.present ? "success" : "error"}
                                    />
                                  );
                                })}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </MUITable>
                  </TableContainer>
                ) : (
                  // When a single employee is selected → same three‐column table
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, px: 2, pt: 2 }}
                    >
                      {summary[0].employee}
                    </Typography>
                    <MUITable size="medium" padding="normal">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Employee
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Present/Absent (in days)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const row = summary[0];
                          const presentCount = (row.presentDates || []).length;
                          const absentCount = (row.absentDates || []).length;
                          const combined = [
                            ...(row.presentDates || []).map((d) => ({
                              date: d,
                              present: true,
                            })),
                            ...(row.absentDates || []).map((d) => ({
                              date: d,
                              present: false,
                            })),
                          ];
                          combined.sort((a, b) => {
                            return (
                              moment(a.date, "YYYY-MM-DD").date() -
                              moment(b.date, "YYYY-MM-DD").date()
                            );
                          });
                          return (
                            <TableRow>
                              <TableCell sx={{ py: 1.5 }}>
                                {row.employee}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {presentCount}/{absentCount}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {combined.map((obj) => {
                                  const dayNumber = moment(
                                    obj.date,
                                    "YYYY-MM-DD"
                                  ).date();
                                  return (
                                    <Chip
                                      key={obj.date}
                                      label={dayNumber}
                                      size="small"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                      color={obj.present ? "success" : "error"}
                                    />
                                  );
                                })}
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </MUITable>
                  </TableContainer>
                )}

                {/* ── Legend Indicator ─────────────────────────── */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    mb: 2,
                    px: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    
                  </Typography>
                  <Chip
                    label="Present"
                    size="small"
                    color="success"
                    sx={{ mr: 1 }}
                  />
                  <Chip label="Absent" size="small" color="error" />
                </Box>
              </>
            ) : (
              <Typography align="center" sx={{ mt: 2 }}>
                No summary to display. Please apply a filter.
              </Typography>
            )}
          </div>
        </div>
      )}

      {/* ───── Daily Details Tab ────────────────────────────────────────── */}
      {activeTab === 0 && (
        <div className="card shadow mb-4">
          <div className="card-body">
            <Typography variant="h5" gutterBottom className="mb-3">
              <CalendarTodayIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Daily Attendance Details
            </Typography>

            {fullRecords.length === 0 ? (
              <Typography align="center">Loading records…</Typography>
            ) : datesForPage.length === 0 ? (
              <Typography align="center">No records on this page.</Typography>
            ) : (
              datesForPage.map((date) => {
                const recs = recordsByDate[date];
                return (
                  <div
                    className="card mb-1 shadow-sm"
                    key={date}
                    style={{ marginBottom: 8 }}
                  >
                    <div
                      className="card-header"
                      style={{ padding: "4px 12px" }}
                    >
                      <Typography variant="h6">{date}</Typography>
                    </div>
                    <div className="card-body">
                      <Box sx={{ overflowX: "auto", mt: -1 }}>
                        <TableContainer component={Paper}>
                          <MUITable size="small" padding="normal">
                            <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Employee
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Status
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Attendance
                                </TableCell>
                                {!isSmall && (
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    Address
                                  </TableCell>
                                )}
                                {!isSmall && (
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    Shift
                                  </TableCell>
                                )}
                                {!isSmall && (
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    Department
                                  </TableCell>
                                )}
                                {!isSmall && (
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    Role
                                  </TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Total Time
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {recs.map((r) => (
                                <TableRow key={r.id}>
                                  <TableCell>{r.employee}</TableCell>
                                  <TableCell>{r.currentStatus}</TableCell>
                                  <TableCell>
                                    {r.checkInTime
                                      ? `In: ${r.checkInTime} / Out: ${
                                          r.checkOutTime || "—"
                                        }`
                                      : "N/A"}
                                  </TableCell>
                                  {!isSmall && (
                                    <TableCell>{r.address}</TableCell>
                                  )}
                                  {!isSmall && (
                                    <TableCell>{r.shift}</TableCell>
                                  )}
                                  {!isSmall && (
                                    <TableCell>{r.department}</TableCell>
                                  )}
                                  {!isSmall && <TableCell>{r.role}</TableCell>}
                                  <TableCell>{r.totalTime}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </MUITable>
                        </TableContainer>
                      </Box>
                    </div>
                  </div>
                );
              })
            )}

            <div className="d-flex justify-content-center mt-3">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, newPage) => setCurrentPage(newPage)}
                color="primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* Snackbar for Export Confirmation */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Exported to Excel!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AttendancePage;
